import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export interface PlatformBranding {
  app_name: string;
  logo_light_url: string | null;
  logo_dark_url: string | null;
  favicon_url: string | null;
  is_customized: boolean;
  updated_at: string | null;
}

export interface PlatformBrandingDraft {
  app_name: string;
  logo_light?: File | null;
  logo_dark?: File | null;
  favicon?: File | null;
  remove_logo_light?: boolean;
  remove_logo_dark?: boolean;
  remove_favicon?: boolean;
}

export const DEFAULT_PLATFORM_BRANDING: PlatformBranding = {
  app_name: 'Zaptor',
  logo_light_url: null,
  logo_dark_url: null,
  favicon_url: null,
  is_customized: false,
  updated_at: null,
};

export const PLATFORM_BRANDING_QUERY_KEY = ['platform-branding'] as const;

async function fetchBranding(): Promise<{ data: PlatformBranding }> {
  const response = await fetch('/api/branding', { cache: 'no-store' });
  if (!response.ok) throw new Error('Não foi possível carregar a identidade do aplicativo.');
  return response.json() as Promise<{ data: PlatformBranding }>;
}

export function usePlatformBranding() {
  const query = useQuery({
    queryKey: PLATFORM_BRANDING_QUERY_KEY,
    queryFn: fetchBranding,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  return { ...query, branding: query.data?.data ?? DEFAULT_PLATFORM_BRANDING };
}

export function useSavePlatformBranding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft: PlatformBrandingDraft) => {
      const form = new FormData();
      form.set('app_name', draft.app_name);
      if (draft.logo_light) form.set('logo_light', draft.logo_light);
      if (draft.logo_dark) form.set('logo_dark', draft.logo_dark);
      if (draft.favicon) form.set('favicon', draft.favicon);
      if (draft.remove_logo_light) form.set('remove_logo_light', 'true');
      if (draft.remove_logo_dark) form.set('remove_logo_dark', 'true');
      if (draft.remove_favicon) form.set('remove_favicon', 'true');
      return apiFetch<{ data: PlatformBranding }>('/api/admin/branding', {
        method: 'PUT',
        body: form,
      });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(PLATFORM_BRANDING_QUERY_KEY, result);
    },
  });
}

export function useResetPlatformBranding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ data: PlatformBranding }>('/api/admin/branding', { method: 'DELETE' }),
    onSuccess: (result) => {
      queryClient.setQueryData(PLATFORM_BRANDING_QUERY_KEY, result);
    },
  });
}
